import { useEffect, useMemo, useRef, useState } from "react";
import type { CapabilityReport } from "../media/capabilities";
import {
  exportCapabilityError,
  inspectCapabilities,
} from "../media/capabilities";
import {
  estimateOutputBytes,
  exportProjectToMp4,
  type ExportProgress,
  type ExportResult,
} from "../media/exporter";
import type { EditingProject } from "../types/project";
import { OUTPUT_TTL_MS } from "../types/project";
import { formatFrames } from "../core/time";
import { saveExportRecord } from "../storage/database";

type Props = {
  project: EditingProject;
  file: File;
  onProjectChange: (project: EditingProject) => void;
  onClose: () => void;
  onCompleted: (result: ExportResult) => void;
  onError: (message: string) => void;
};

export function ExportSheet({
  project,
  file,
  onProjectChange,
  onClose,
  onCompleted,
  onError,
}: Props) {
  const [capability, setCapability] = useState<CapabilityReport | null>(null);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [result, setResult] = useState<ExportResult | null>(null);
  const controller = useRef<AbortController | null>(null);
  const selectionFrames = project.endFrame - project.startFrame;
  const estimated = useMemo(
    () => estimateOutputBytes(selectionFrames, project.sampleRate),
    [selectionFrames, project.sampleRate],
  );

  useEffect(() => {
    void inspectCapabilities(project.sampleRate, project.channels)
      .then(setCapability)
      .catch((error: unknown) => onError(messageOf(error)));
    return () => controller.current?.abort();
  }, [project.sampleRate, project.channels, onError]);

  const create = async (): Promise<void> => {
    controller.current = new AbortController();
    setResult(null);
    try {
      const exported = await exportProjectToMp4(file, project, {
        title: project.title,
        suppressClicks: project.suppressClicks,
        signal: controller.current.signal,
        onProgress: setProgress,
      });
      await saveExportRecord({
        id: crypto.randomUUID(),
        projectId: project.id,
        fileName: exported.file.name,
        opfsName: exported.opfsName,
        createdAt: Date.now(),
        expiresAt: Date.now() + OUTPUT_TTL_MS,
        size: exported.file.size,
        durationFrames: exported.durationFrames,
        mimeType: exported.mimeType,
      }).catch(() => undefined);
      setResult(exported);
      setProgress(null);
      onCompleted(exported);
    } catch (error) {
      setProgress(null);
      if (error instanceof DOMException && error.name === "AbortError")
        onError("動画生成をキャンセルしました。編集内容は保持されています。");
      else onError(messageOf(error));
    } finally {
      controller.current = null;
    }
  };

  const share = async (): Promise<void> => {
    if (!result) return;
    try {
      const data: ShareData = { title: "VoiceCutter", files: [result.file] };
      if (navigator.canShare?.(data)) await navigator.share(data);
      else download(result.file);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError"))
        onError(messageOf(error));
    }
  };

  return (
    <div className="sheet-backdrop" role="presentation">
      <section
        className="sheet export-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-title"
        data-testid="export.sheet"
      >
        <div className="sheet__grabber" aria-hidden="true" />
        <header className="sheet__header">
          <div>
            <p className="eyebrow">MEDIA EXPORT</p>
            <h2 id="export-title">動画を作成</h2>
          </div>
          {!progress && (
            <button
              type="button"
              className="icon-button"
              aria-label="閉じる"
              onClick={onClose}
            >
              ×
            </button>
          )}
        </header>

        {progress ? (
          <div className="export-progress" aria-live="polite">
            <div
              className="progress-ring"
              style={
                {
                  "--progress": `${progress.progress * 360}deg`,
                } as React.CSSProperties
              }
            >
              <strong>{Math.round(progress.progress * 100)}%</strong>
            </div>
            <h3>{progress.phase}</h3>
            <p>
              経過 {formatElapsed(progress.elapsedSeconds)}
              {progress.etaSeconds !== null && progress.etaSeconds > 1
                ? `・残り目安 ${formatElapsed(progress.etaSeconds)}`
                : ""}
            </p>
            <p className="export-foreground-note">
              完了までVoiceCutterを前面にしてください
            </p>
            <button
              type="button"
              className="cancel-button"
              onClick={() => controller.current?.abort()}
            >
              キャンセル
            </button>
          </div>
        ) : result ? (
          <div className="export-complete">
            <div className="success-mark" aria-hidden="true">
              ✓
            </div>
            <h3>MP4が完成しました</h3>
            <p>
              {formatBytes(result.file.size)}・
              {formatFrames(result.durationFrames, project.sampleRate)}
            </p>
            <VideoPreview file={result.file} />
            <button
              type="button"
              className="confirm-button"
              onClick={() => void share()}
            >
              共有・保存
            </button>
            <button
              type="button"
              className="secondary-button"
              onClick={() => download(result.file)}
            >
              ダウンロード
            </button>
            <button type="button" className="text-button" onClick={onClose}>
              編集へ戻る
            </button>
          </div>
        ) : (
          <div className="export-config">
            <div className="export-summary">
              <span>選択時間</span>
              <strong>
                {formatFrames(selectionFrames, project.sampleRate)}
              </strong>
              <small>推定サイズ 約{formatBytes(estimated)}</small>
            </div>

            <label className="field">
              <span>中央タイトル（最大2行・空欄可）</span>
              <input
                value={project.title}
                maxLength={100}
                onChange={(event) =>
                  onProjectChange({
                    ...project,
                    title: event.target.value,
                    updatedAt: Date.now(),
                  })
                }
              />
            </label>
            <label className="toggle-card">
              <span>
                <strong>切断ノイズを抑える</strong>
                <small>境界位置を動かさず、両端に約2msの処理</small>
              </span>
              <input
                type="checkbox"
                checked={project.suppressClicks}
                onChange={(event) =>
                  onProjectChange({
                    ...project,
                    suppressClicks: event.target.checked,
                    updatedAt: Date.now(),
                  })
                }
              />
            </label>

            {capability ? (
              exportCapabilityError(capability) ? (
                <div className="error-banner" role="alert">
                  {exportCapabilityError(capability)}
                </div>
              ) : (
                <div className="capability-ok">
                  H.264 / AAC / MP4生成に対応（AAC:{" "}
                  {capability.aacMode === "native"
                    ? "端末内蔵"
                    : "ローカルWASM"}
                  ）
                </div>
              )
            ) : (
              <p className="muted">動画生成機能を確認中…</p>
            )}

            <button
              type="button"
              className="confirm-button export-create"
              disabled={
                !capability || Boolean(exportCapabilityError(capability))
              }
              onClick={() => void create()}
              data-testid="export.create"
            >
              MP4を作成
            </button>
            <p className="privacy-note">すべての処理はこの端末内で行われます</p>
          </div>
        )}
      </section>
    </div>
  );
}

function download(file: File): void {
  const url = URL.createObjectURL(file);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = file.name;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function VideoPreview({ file }: { file: File }) {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return (
    <video
      controls
      playsInline
      src={url}
      aria-label="生成した動画のプレビュー"
    />
  );
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function formatElapsed(seconds: number): string {
  const rounded = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(rounded / 60);
  const rest = rounded % 60;
  return minutes > 0 ? `${minutes}分${rest}秒` : `${rest}秒`;
}
